import { useEffect, useState } from "react";
import { Truck, CheckCircle2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Delivery, DeliveryStatusEnum } from "@/lib/types/delivery.types";
import { DeliveryStatusBadge } from "../job-page/job-page-components/deliveries/delivery-status-badge.component";
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogCancel,
	AlertDialogAction,
} from "@/dentlab/src/components/ui/alert-dialog";
import { Logger } from "@/lib/logger/Logger";

interface VendorJobDeliveriesProps {
	jobId: number;
	canMarkDelivered?: boolean;
}

const formatDate = (dateStr: string | null) => {
	if (!dateStr) return null;
	return new Date(dateStr).toLocaleDateString("de-CH", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

export const VendorJobDeliveries: React.FC<VendorJobDeliveriesProps> = ({
	jobId,
	canMarkDelivered,
}) => {
	const [deliveries, setDeliveries] = useState<Delivery[]>([]);
	const [courierNames, setCourierNames] = useState<Record<string, string>>(
		{}
	);
	const [loading, setLoading] = useState(true);
	const [confirmId, setConfirmId] = useState<number | null>(null);
	const [marking, setMarking] = useState(false);

	useEffect(() => {
		const fetch = async () => {
			const { data } = await supabase
				.from("deliveries")
				.select("*")
				.eq("job_id", jobId)
				.order("created_at", { ascending: false });
			const dels = (data as Delivery[]) ?? [];
			setDeliveries(dels);

			const profileIds = [
				...new Set(
					dels
						.filter(
							(d) => d.courier_type === "person" && d.profile_id
						)
						.map((d) => d.profile_id!)
				),
			];
			if (profileIds.length > 0) {
				const { data: profiles } = await supabase
					.from("profiles")
					.select("id, first_name, last_name")
					.in("id", profileIds);
				if (profiles) {
					const names: Record<string, string> = {};
					for (const p of profiles) {
						names[p.id] = `${p.first_name ?? ""} ${
							p.last_name ?? ""
						}`.trim();
					}
					setCourierNames(names);
				}
			}
			setLoading(false);
		};
		fetch();
	}, [jobId]);

	const getCourierName = (d: Delivery) => {
		if (d.courier_type === "person" && d.profile_id) {
			return courierNames[d.profile_id] || "Kurier";
		}
		return d.courier_company_name || "Kurier";
	};

	const handleMarkDelivered = async (deliveryId: number) => {
		setMarking(true);
		const { error } = await supabase.rpc(
			"mark_delivery_delivered_by_customer",
			{ p_delivery_id: deliveryId }
		);
		if (error) {
			Logger.error(error, {}, "Error marking delivery as delivered");
		} else {
			setDeliveries((prev) =>
				prev.map((d) =>
					d.id === deliveryId
						? {
								...d,
								status: DeliveryStatusEnum.DELIVERED,
								delivered_at: new Date().toISOString(),
							}
						: d
				)
			);
		}
		setMarking(false);
		setConfirmId(null);
	};

	if (loading) {
		return (
			<div className="rounded-lg border border-stone-200 p-4">
				<div className="flex items-center gap-2 mb-3">
					<Truck size={16} className="text-stone-400" />
					<h3 className="font-medium text-stone-800">Lieferungen</h3>
				</div>
				<p className="text-xs text-stone-400">Laden...</p>
			</div>
		);
	}

	if (deliveries.length === 0) {
		return (
			<div className="rounded-lg border border-stone-200 p-4">
				<div className="flex items-center gap-2 mb-3">
					<Truck size={16} className="text-stone-400" />
					<h3 className="font-medium text-stone-800">Lieferungen</h3>
				</div>
				<p className="text-sm text-stone-500">Keine Lieferungen</p>
			</div>
		);
	}

	const isActive = (status: DeliveryStatusEnum) =>
		status === DeliveryStatusEnum.PENDING ||
		status === DeliveryStatusEnum.IN_TRANSIT;

	return (
		<>
			<div className="rounded-lg border border-stone-200 overflow-hidden">
				<div className="flex items-center gap-2 px-4 py-3 border-b border-stone-200">
					<Truck size={16} className="text-stone-400" />
					<h3 className="font-medium text-stone-800">
						Lieferungen ({deliveries.length})
					</h3>
				</div>
				<div className="divide-y divide-stone-100">
					{deliveries.map((d) => (
						<div
							key={d.id}
							className="px-4 py-3 space-y-1"
							data-testid={`vendor-delivery-${d.id}`}
						>
							<div className="flex items-center justify-between">
								<DeliveryStatusBadge
									status={d.status}
									id={d.id}
								/>
								<span className="text-xs text-stone-400">
									{formatDate(d.created_at)}
								</span>
							</div>
							<p className="text-xs text-stone-500">
								{d.courier_type === "person"
									? "Kurier"
									: "Versand"}
								:{" "}
								<span className="text-stone-700">
									{getCourierName(d)}
								</span>
							</p>
							{d.description && (
								<p className="text-xs text-stone-600 line-clamp-2">
									{d.description}
								</p>
							)}
							{d.scheduled_departure_at && !d.delivered_at && (
								<p className="text-xs text-stone-400">
									Geplant:{" "}
									{formatDate(d.scheduled_departure_at)}
								</p>
							)}
							{d.delivered_at && (
								<p className="text-xs text-green-600">
									Zugestellt: {formatDate(d.delivered_at)}
								</p>
							)}
							{canMarkDelivered && isActive(d.status) && (
								<button
									className="flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 transition-colors w-fit"
									onClick={() => setConfirmId(d.id)}
									data-testid={`mark-delivered-vendor-${d.id}`}
									aria-label="Als zugestellt markieren"
								>
									<CheckCircle2 className="h-3.5 w-3.5" />
									Als zugestellt markieren
								</button>
							)}
						</div>
					))}
				</div>
			</div>
			<AlertDialog
				open={confirmId !== null}
				onOpenChange={(open) => {
					if (!open) setConfirmId(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							Lieferung als zugestellt markieren?
						</AlertDialogTitle>
						<AlertDialogDescription>
							Die Lieferung wird als zugestellt markiert und das
							Labor wird benachrichtigt.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={marking}>
							Abbrechen
						</AlertDialogCancel>
						<AlertDialogAction
							disabled={marking}
							onClick={async () => {
								if (confirmId === null) return;
								await handleMarkDelivered(confirmId);
							}}
						>
							{marking ? "Markiere..." : "Zugestellt"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
};
